/**
 * ApiResponseViewerControl
 * Displays JSON/API response data in a formatted, copyable code block.
 * Value: any JSON-serializable value.
 */

import React, { useState, memo, useCallback, useMemo } from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import { CopyIcon, CheckIcon } from '@atlas-forms/ui-components-react';

export interface ApiResponseViewerControlProps {
  control: FormControl;
  value?: unknown;
  mode?: 'edit' | 'view';
}

export const ApiResponseViewerControl = memo(function ApiResponseViewerControl({ control, value, mode = 'edit' }: ApiResponseViewerControlProps) {
  const [copied, setCopied] = useState(false);
  const cfg = (control as any).config ?? {};
  const maxHeight: number = cfg.maxHeight ?? 320;

  const formatted = useMemo(() => {
    if (value === undefined || value === null) return mode === 'edit' ? '// No data' : '';
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }, [value, mode]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(formatted).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [formatted]);

  const wrapStyle: React.CSSProperties = {
    position: 'relative',
    borderRadius: 8,
    border: '1px solid rgba(51,65,85,0.3)',
    background: 'rgba(2,6,23,0.8)',
    overflow: 'hidden',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid rgba(51,65,85,0.3)',
    background: 'rgba(15,23,42,0.6)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const copyBtnStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: copied ? '#22c55e' : '#64748b',
    fontSize: 12,
    padding: '2px 6px',
    borderRadius: 4,
    transition: 'color 0.15s',
  };

  const preStyle: React.CSSProperties = {
    margin: 0,
    padding: '12px',
    overflowY: 'auto',
    maxHeight,
    fontSize: 12,
    lineHeight: 1.6,
    color: '#a5f3fc',
    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  return (
    <div data-control-id={control.id} data-control-type="api-response-viewer" style={wrapStyle}>
      <div style={labelStyle}>
        <span style={titleStyle}>{control.label ?? 'Response'}</span>
        <button type="button" onClick={handleCopy} style={copyBtnStyle} title="Copy to clipboard">
          {copied ? <CheckIcon width={14} height={14} /> : <CopyIcon width={14} height={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre style={preStyle}>{formatted}</pre>
    </div>
  );
});
